import {
  AssignmentTurnedInOutlined,
  ForwardToInboxOutlined,
} from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { enqueueSnackbar, closeSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import api from "src/shared/api";
import {
  IconComponent,
  LoadingButton,
  EConfirmModalType,
} from "src/shared/components";
import { useConfirmModal } from "src/shared/hooks";
import { TableCheckedState } from "src/shared/hooks/useGetTableCheckboxesState";
import { parseAgency } from "src/shared/lib/agency/parseAgency";
import { Can } from "src/shared/lib/rback/Can";
import { AGENCY_LABEL_MAP } from "src/shared/types/common.types";
import { EIconNames } from "src/shared/types/iconNames";
import {
  useFormActsMutation,
  useLazyExportSummaryReportQuery,
  useLazyGetFormedActsZipQuery,
  usePostBonusStatesMutation,
  useSendActsToEmailMutation,
} from "../model/api/api";
import { getPostBonusStateErrors } from "../model/helpers/errors.helpers";
import {
  EBonusState,
  FormingActsZipStatus,
  IBonusStateItem,
  ISummaryReportItem,
} from "../model/types";

interface ITableHeaderProps {
  tableCheckedState: TableCheckedState;
  tableEmailsCheckedState: TableCheckedState;
  rows: ISummaryReportItem[];
  resetEmailsCheckedState: () => void;
  resetTableCheckedState: () => void;
}

interface IExportSummaryReportQueryParams {
  report_month: string;
  states?: string;
  agency?: string | null;
}

export function TableHeader({
  tableCheckedState,
  tableEmailsCheckedState,
  rows,
  resetTableCheckedState,
  resetEmailsCheckedState,
}: ITableHeaderProps) {
  const [isExporting, setIsExporting] = useState(false);
  const [searchParams] = useSearchParams();
  const [postBonusStateRequest, { isLoading: isLoadingPostBonusState }] =
    usePostBonusStatesMutation();
  const [sendActsToEmail, { isLoading: isLoadingSendActsToEmail }] =
    useSendActsToEmailMutation();
  const [formActsRequest, { isLoading: isLoadingFormActs }] =
    useFormActsMutation();
  const [exportSummaryReport] = useLazyExportSummaryReportQuery();
  const [getFormedActsZip] = useLazyGetFormedActsZipQuery();
  const agency = parseAgency(searchParams.get("agency"));
  const agencyLabel = agency !== null ? AGENCY_LABEL_MAP[agency] : null;
  const canShowFormActs = agency !== null;
  const formActsButtonTitle = agencyLabel
    ? `Сформировать акты ${agencyLabel}`
    : "";

  async function postBonusState(data: IBonusStateItem[]) {
    try {
      const res = await postBonusStateRequest(data).unwrap();
      const errors = getPostBonusStateErrors(res.data);
      if (errors.length) {
        enqueueSnackbar(errors.join("\n"), {
          variant: "error",
          handleClose: closeSnackbar,
        });
      }
    } catch {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    } finally {
      resetTableCheckedState();
    }
  }

  async function formActs() {
    if (agency === null) {
      enqueueSnackbar("Выберите агенство", { variant: "warning" });

      return;
    }

    try {
      const res = await formActsRequest({ agency }).unwrap();
      if (res.success) {
        enqueueSnackbar(res.message, {
          variant: "success",
          handleClose: closeSnackbar,
        });
      }
    } catch {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }
  async function handlePostBonusesToEmail(closeModal?: () => void) {
    try {
      const data = { data: checkedEmailsIds.map((id) => ({ id })) };
      await sendActsToEmail({ data }); // Ожидаем завершения запроса

      enqueueSnackbar("Aкты успешно отправлены", {
        variant: "success",
        handleClose: closeSnackbar,
      });

      resetEmailsCheckedState();
      closeModal?.();
    } catch (error) {
      console.error("Ошибка при отправке актов:", error);
      enqueueSnackbar("Не удалось отправить акты", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  }
  const checkedEmailsIds = useMemo(() => {
    if (tableEmailsCheckedState.all) {
      return rows.reduce((acc: number[], item) => {
        if (
          !tableEmailsCheckedState.uncheckedIds.has(String(item.id)) &&
          item.state === EBonusState.ACT_CARRIED_OUT
        ) {
          return [...acc, item.id];
        }

        return acc;
      }, []);
    } else {
      return Array.from(tableEmailsCheckedState.checkedIds);
    }
  }, [tableEmailsCheckedState, rows]);

  const checkedActsIds = rows.reduce((acc: number[], item) => {
    if (item.state === EBonusState.CHECKED) {
      return [...acc, item.id];
    }

    return acc;
  }, []);

  const readyForSaveActIds = useMemo(() => {
    if (tableCheckedState.all) {
      return rows.reduce((acc: number[], item) => {
        if (
          item.state === EBonusState.CREATED &&
          !tableCheckedState.uncheckedIds.has(String(item.id))
        ) {
          return [...acc, item.id];
        }

        return acc;
      }, []);
    } else {
      return Array.from(tableCheckedState.checkedIds);
    }
  }, [tableCheckedState, rows]);

  const { modal, showModal } = useConfirmModal({
    onResolve: ({ closeModal }) => handlePostBonusesToEmail(closeModal),
    confirmModalType: EConfirmModalType.WARNING,
    confirmBtnProps: {
      isLoading: isLoadingSendActsToEmail,
      title: "Отправить",
    },
    title: "Отправить акты Партнеров на email?",
  });

  const handleExportSummaryReport = async () => {
    const report_month = searchParams.get("report_month");
    const states = searchParams.get("states");
    const agency = searchParams.get("agency");
    const FILE_NAME = "Сводный_отчет_по_бонусам_Партнеров_";

    if (!report_month) {
      enqueueSnackbar("Выберите месяц для экспорта", {
        variant: "warning",
        handleClose: closeSnackbar,
      });

      return;
    }

    try {
      setIsExporting(true);

      const queryParams: IExportSummaryReportQueryParams = {
        report_month,
        agency,
      };

      if (states) {
        queryParams.states = states;
      }

      const result = await exportSummaryReport(queryParams);

      if (result.data) {
        const url = window.URL.createObjectURL(new Blob([result.data]));
        const link = document.createElement("a");
        const resultFileName = `${FILE_NAME}${report_month}`;
        link.href = url;
        link.setAttribute("download", `${resultFileName}.csv`); // Имя файла
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    } finally {
      setIsExporting(false);
    }
  };

  const isBonusesLoading = useSelector(
    (state) =>
      //@ts-expect-error Это нужно что бы не типизировать все эндпоинты
      api.endpoints.updateBonus.select({ fixedCacheKey: "updateBonus" })(state)
        ?.status === "pending",
  );

  const handleActsZipDownload = async () => {
    try {
      const report_month = searchParams.get("report_month");
      if (!report_month) {
        return enqueueSnackbar("Задайте период для скачивания актов", {
          variant: "warning",
          handleClose: closeSnackbar,
        });
      }
      const result = await getFormedActsZip(report_month).unwrap();
      if (result.status === FormingActsZipStatus.IN_PROCESS) {
        enqueueSnackbar(
          "Формирование актов в процессе. Пожалуйста, повторите попытку через 5 минут",
          {
            variant: "info",
          },
        );

        return;
      }

      if (result.status === FormingActsZipStatus.NOT_EXISTS) {
        enqueueSnackbar("Пожалуйста, сформируйте акты", {
          variant: "info",
        });

        return;
      }

      if (result.status === FormingActsZipStatus.PENDING) {
        enqueueSnackbar(
          "Ваш запрос поставлен в очередь. По завершении вам будет отправлено сообщение в ERP со ссылкой на архив.",
          {
            variant: "info",
          },
        );

        return;
      }

      const link = document.createElement("a");
      link.href = result.path;
      const filename = "formed_acts.zip";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      enqueueSnackbar("Произошла ошибка", {
        variant: "error",
        handleClose: closeSnackbar,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 3,
        }}
      >
        <Typography variant="h6">Сводный отчет по бонусам Партнеров</Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 3,
            ml: "auto",
          }}
        >
          <Can perform="partners.summary-report.export">
            <Button
              onClick={handleExportSummaryReport}
              startIcon={<IconComponent iconname={EIconNames.DOWNLOAD} />}
              variant="outlined"
              disabled={isBonusesLoading || isExporting}
            >
              Экспорт
            </Button>
          </Can>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 3,
          }}
        >
          <Can perform="partners.summary-report.form-act">
            {canShowFormActs && (
              <LoadingButton
                isLoading={isLoadingFormActs}
                startIcon={<AssignmentTurnedInOutlined />}
                variant="contained"
                disabled={checkedActsIds.length === 0}
                onClick={formActs}
              >
                {formActsButtonTitle}
              </LoadingButton>
            )}
          </Can>
          <Can perform="partners.summary-report.edit">
            <Button
              onClick={showModal}
              startIcon={<ForwardToInboxOutlined />}
              variant="outlined"
              disabled={checkedEmailsIds.length === 0}
            >
              Отправить на email ({checkedEmailsIds.length})
            </Button>
          </Can>
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 3,
          }}
        >
          {!!readyForSaveActIds.length && (
            <Can perform="partners.summary-report.edit">
              <Button
                onClick={() =>
                  postBonusState(
                    readyForSaveActIds.map((id) => ({
                      id: Number(id),
                      is_checked: true,
                    })),
                  )
                }
                disabled={
                  readyForSaveActIds.length === 0 || isLoadingPostBonusState
                }
                variant="outlined"
              >
                {`Сохранить${readyForSaveActIds.length ? " (" + readyForSaveActIds.length + ")" : ""}`}
              </Button>
            </Can>
          )}
          <Can perform="partners.summary-report.edit">
            <Button
              onClick={handleActsZipDownload}
              startIcon={<IconComponent iconname={EIconNames.DOWNLOAD} />}
              variant="text"
              sx={{ color: "grey.700" }}
            >
              Скачать акты
            </Button>
          </Can>
        </Box>
      </Box>
      {modal}
    </Box>
  );
}
